import { useEffect, useMemo, useState, useRef } from "react";
import { useDashboardUI } from "../../context/GlobalContext";
import { useNavigate, useParams } from "react-router-dom";
import assistantService from "../../services/assistantService";
import UploadAssistantFileModal from "./UploadAssistantFilesModal";
import { getActualRagFilename, previewRagFile } from "../../helpers/helpers";
import { Trash2, Eye } from "lucide-react";
import fileService from "../../services/fileService";



export default function AssistantFileList() {
    const { setPageLoader } = useDashboardUI();
    const navigate = useNavigate();
    const { assistantId } = useParams();

    const [assistantName, setAssistantName] = useState("");
    const [files, setFiles] = useState([]);
    const [search, setSearch] = useState("");
    const [openActionMenu, setOpenActionMenu] = useState(null);
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const actionMenuRef = useRef(null);
    const [loadingFile, setLoadingFile] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);




    const fetchAssistantFileList = async () => {
        try {
            setPageLoader(true);
            const data = await assistantService.getAssistantFileList(
                assistantId
            );

            setAssistantName(data?.assistant_name);
            setFiles(data?.file_list || []);
        } catch (error) {
            console.error("Error fetching assistant files:", error);
        } finally {
            setPageLoader(false);
        }
    };


    // Fetch files
    useEffect(() => {
        if (!assistantId) return;
        fetchAssistantFileList();
    }, [assistantId]);

    // Client-side search
    const filteredFiles = useMemo(() => {
        if (!search) return files;

        return files.filter((file) =>
            `${file.file_name} ${file.file_description || ""}`
                .toLowerCase()
                .includes(search.toLowerCase())
        );
    }, [search, files]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                actionMenuRef.current &&
                !actionMenuRef.current.contains(event.target)
            ) {
                setOpenActionMenu(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const deleteFile = async (selectedFile) => {
        try {
            setPageLoader(true);
            const file_ids = files?.map(f => f.file_id)
            const remaining_file = file_ids.filter(
                (id) => selectedFile.file_id !== id
            )
            await assistantService.updateAssistant(assistantId, { "file_ids": remaining_file });

            await fileService.deleteFile(selectedFile.file_id)

            setFiles(prev =>
                prev.filter(f => f.file_id !== selectedFile.file_id)
            )

            setShowDeleteModal(false);
            setPageLoader(false);
        } catch (err) {
            setPageLoader(false);
            console.error("Delete error:", err);
        }
    }


    return (
        <>
            <div className="w-full flex flex-col h-full lg:h-[calc(100vh-100px)] pt-4 gap-3">

                {/* Header */}
                {/* Optional: Assistant name display (small + subtle) */}
                <div className="hidden sm:block flex-shrink-0 text-sm text-[#4B5266]">
                    Assistant Name: <span className="font-semibold text-[#14122B]">{assistantName}</span>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 flex-shrink-0">

                    {/* Search - LEFT */}
                    <input
                        type="text"
                        placeholder="Search files by name or description"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full min-w-0 sm:w-80 h-10 sm:h-11 rounded-xl border border-[#ECEEF4] bg-white px-4 sm:px-5 text-sm text-gray-700 placeholder:text-[#9AA1B2] transition-colors hover:border-[#D9DCE6] focus:outline-none focus:border-[#8B5CF6]/50 focus:ring-2 focus:ring-[#8B5CF6]/10"
                    />

                    {/* Right Side Actions */}
                    <div className="flex w-full items-center gap-3 sm:w-auto">

                        {/* Create Button */}
                        <button
                            onClick={() => setOpenUploadModal(true)}
                            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-[#3F4BB8] px-4 sm:px-6 h-11 text-sm font-semibold text-white hover:bg-[#363FA0] transition sm:w-auto"
                        >
                            Create<span className="hidden sm:inline"> Assistant File</span>
                            <span className="text-lg">+</span>
                        </button>

                    </div>
                </div>


                {/* Table card (same look as the other tables) */}
                <div className="flex-1 min-h-0 bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
                <div className="table-scroll h-full overflow-auto">
                    <table className="w-full min-w-[640px] table-fixed border-collapse text-sm text-left">
                        <colgroup>
                            <col style={{ width: "34%" }} />
                            <col style={{ width: "22%" }} />
                            <col style={{ width: "22%" }} />
                            <col style={{ width: "22%" }} />
                        </colgroup>
                        <thead>
                            <tr className="border-b border-gray-100">
                                {[
                                    { label: "File name", align: "left" },
                                    { label: "Type", align: "left" },
                                    { label: "Size", align: "left" },
                                    { label: "Action", align: "center" },
                                ].map(({ label, align }) => (
                                    <th
                                        key={label}
                                        style={{ position: "sticky", top: 0, background: "#F5F2FF", zIndex: 20, textAlign: align }}
                                        className={`py-3.5 align-middle text-xs font-semibold text-gray-500 uppercase tracking-wide ${align === "center" ? "px-3" : "pl-6 pr-3"}`}
                                    >
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredFiles.length > 0 ? (
                                filteredFiles.map((file, index) => (
                                    <tr
                                        key={file.file_id}
                                        className="cursor-pointer border-b border-gray-100 last:border-0 transition-colors hover:bg-gray-50"
                                    >
                                        <td className="pl-6 pr-3 py-4 align-middle text-sm font-semibold text-[#14122B] break-words">
                                            {(() => {
                                                const name = getActualRagFilename(file.file_name);
                                                return name.length > 100 ? name.slice(0, 100) + "..." : name;
                                            })()}
                                        </td>

                                        <td className="pl-6 pr-3 py-4 align-middle text-sm font-medium text-gray-600">
                                            {file.file_type}
                                        </td>

                                        <td className="pl-6 pr-3 py-4 align-middle text-sm font-medium text-gray-600 whitespace-nowrap">
                                            {(file.file_size / 1024).toFixed(1)} KB
                                        </td>

                                        <td className="relative px-3 py-4 align-middle text-center">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenActionMenu(
                                                        openActionMenu === index ? null : index
                                                    );
                                                }}
                                                className="rounded-full px-3 py-1 text-2xl font-bold leading-none text-gray-500 hover:bg-gray-100"
                                            >
                                                ⋮
                                            </button>

                                            {openActionMenu === index && (
                                                <div
                                                    ref={actionMenuRef}
                                                    className="absolute right-6 top-12 z-30 w-48 rounded-xl border border-[#ECEEF4] bg-white text-left shadow-lg"
                                                >



                                                    <button
                                                        onClick={previewRagFile(
                                                            file.blob_url,
                                                            file.source_type,
                                                            setLoadingFile
                                                        )}
                                                        className="flex items-center gap-2 px-4 py-3 text-sm hover:bg-gray-50"
                                                    >
                                                        {loadingFile === file.blob_url ? (
                                                            <span className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full"></span>
                                                        ) : (
                                                            <>
                                                                <Eye size={16} className="text-gray-500" />
                                                                <span>View File</span>
                                                            </>
                                                        )}
                                                    </button>

                                                    <button
                                                        onClick={() => {
                                                            setSelectedFile(file);
                                                            setShowDeleteModal(true);
                                                        }}
                                                        className="flex w-full items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50"
                                                    >
                                                        <Trash2 size={16} />
                                                        Delete File
                                                    </button>

                                                </div>
                                            )}

                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="px-6 py-10 text-center text-sm text-gray-500"
                                    >
                                        No files found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                </div>

                {/* Footer */}
                <div className="mt-auto pt-1 pb-1 text-sm flex-shrink-0">
                    Total Files: {filteredFiles.length}
                </div>

                <UploadAssistantFileModal
                    open={openUploadModal}
                    onClose={() => setOpenUploadModal(false)}
                    assistantId={assistantId}
                    assistantFiles={files}
                    onSuccess={() => {
                        // re-fetch file list after upload
                        fetchAssistantFileList();
                    }}
                />

            </div>

            {showDeleteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="w-[400px] max-w-[calc(100vw-1.5rem)] rounded-2xl bg-white p-6 shadow-xl">

                        <h2 className="text-lg font-semibold">
                            Delete File
                        </h2>

                        <p className="mt-2 text-sm text-gray-600">
                            Are you sure you want to delete{" "}
                            <span className="font-bold">
                                {getActualRagFilename(selectedFile?.file_name)}
                            </span>
                            ? This action cannot be undone.
                        </p>

                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="rounded-lg px-4 py-2 text-sm hover:bg-gray-100"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={async () => {
                                    deleteFile(selectedFile)
                                }}
                                className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
